import React, { useState } from 'react';
import { Container, Typography, Box, Button, Link, IconButton, Tooltip, LinearProgress } from '@mui/material';
import { Logout } from '@mui/icons-material';
import { appUrls, getEnvironmentConfig } from '../config/environment';
import { useSignOut } from '../hooks/useSignOut';

const NoAccessScreen: React.FC = () => {
    const config = getEnvironmentConfig();
    const { signOut } = useSignOut();
    const [isSigningOut, setIsSigningOut] = useState(false);

    const handleOpenApp = () => {
        window.location.href = appUrls.shows;
    };

    const handleManagePatreon = () => {
        window.location.href = config.patreonCampaignUrl;
    };

    const handleSignOut = async () => {
        setIsSigningOut(true);
        try {
            await signOut('/');
        } catch (error) {
            console.error('Sign out failed:', error);
            setIsSigningOut(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                position: 'relative',
            }}
        >
            <Tooltip title="Sign out of this account" placement="left">
                <IconButton
                    onClick={handleSignOut}
                    disabled={isSigningOut}
                    sx={{
                        position: 'absolute',
                        top: (theme) => theme.spacing(2),
                        right: (theme) => theme.spacing(2),
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                            color: 'common.white',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:disabled': {
                            color: 'rgba(255, 255, 255, 0.3)',
                        },
                    }}
                >
                    {isSigningOut ? (
                        <LinearProgress
                            sx={{
                                width: 20,
                                height: 2,
                                borderRadius: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: 'common.white',
                                },
                            }}
                        />
                    ) : (
                        <Logout fontSize="small" />
                    )}
                </IconButton>
            </Tooltip>
            <Container maxWidth="lg" sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle', height: '100vh' }}>
                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{ mb: 3 }}
                    >
                        No Access
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            mb: 4,
                            maxWidth: '600px',
                            color: '#BDBDBD',
                            fontSize: '18px',
                            textAlign: 'center',
                        }}
                    >
                        Your membership is managed outside of this portal. If you subscribed in the app, go to your profile settings to manage your plan.
                        <Box component="br" sx={{ display: { xs: 'block', sm: 'none' } }} />
                        {' '}
                        If you joined through Patreon, you can update your membership there.
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                            width: '100%',
                            maxWidth: '500px',
                            mb: 3,
                            mt: 5,
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleManagePatreon}
                            fullWidth
                            sx={{
                                borderColor: 'primary.main',
                                color: 'text.primary',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            Manage on Patreon
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleOpenApp}
                            fullWidth
                        >
                            Open the App
                        </Button>
                    </Box>

                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.primary',
                            fontSize: '14px',
                            mt: 10,
                        }}
                    >
                        Need Help? Email{' '}
                        <Link
                            href={`mailto:${config.supportEmail}`}
                            sx={{
                                color: 'primary.main',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: 'primary.light',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {config.supportEmail}
                        </Link>
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default NoAccessScreen;

